import React from 'react';

interface ServiceHistory {
  teamId: string;
  teamName: string;
  description: string;
  joinedAt: string;
  position: string;
  totalServices: number;
  lastService: string | null;
  firstService: string | null;
  monthsActive: number;
  frequencyLevel: 'No Services' | 'Occasional' | 'Moderate' | 'Active' | 'Very Active' | 'No Data';
  status: 'Current' | 'Recent' | 'Inactive' | 'Historical' | 'Unknown';
}

interface ServiceHistoryProps {
  serviceHistory: ServiceHistory[];
  memberName?: string;
}

const ServiceHistory: React.FC<ServiceHistoryProps> = ({ serviceHistory, memberName }) => {
  const getFrequencyColor = (level: string) => {
    switch (level) {
      case 'Very Active':
        return 'bg-green-100 text-green-800';
      case 'Active':
        return 'bg-blue-100 text-blue-800';
      case 'Moderate':
        return 'bg-yellow-100 text-yellow-800';
      case 'Occasional':
        return 'bg-orange-100 text-orange-800';
      case 'No Services':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Current':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'Recent':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'Inactive':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Historical':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (!serviceHistory || serviceHistory.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Service History</h3>
          <p className="text-sm text-gray-600 mt-1">Team memberships and service frequency</p>
        </div>
        <div className="p-6 text-center text-gray-500">
          <div className="mb-4">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          </div>
          <p className="text-lg font-medium text-gray-900">No Service History</p>
          <p className="text-sm text-gray-500 mt-1">
            {memberName ? `${memberName} has not served on any teams yet.` : 'No service teams found.'}
          </p>
        </div>
      </div>
    );
  }

  const totalServices = serviceHistory.reduce((sum, service) => sum + service.totalServices, 0);
  const activeTeams = serviceHistory.filter(service => service.status === 'Current').length;
  const totalTeams = serviceHistory.length;

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Service History</h3>
            <p className="text-sm text-gray-600 mt-1">Team memberships and service frequency</p>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-blue-600">{totalServices}</div>
            <div className="text-sm text-gray-500">Total Services</div>
          </div>
        </div>
        
        {/* Summary Stats */}
        <div className="mt-4 grid grid-cols-3 gap-4">
          <div className="text-center">
            <div className="text-lg font-semibold text-gray-900">{totalTeams}</div>
            <div className="text-xs text-gray-500">Total Teams</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-semibold text-green-600">{activeTeams}</div>
            <div className="text-xs text-gray-500">Current Teams</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-semibold text-blue-600">
              {totalTeams > 0 ? Math.round(totalServices / totalTeams) : 0}
            </div>
            <div className="text-xs text-gray-500">Avg Services/Team</div>
          </div>
        </div>
      </div>

      <div className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Team
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Position
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Services
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Frequency
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Duration
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {serviceHistory.map((service, index) => (
                <tr key={`${service.teamId}-${index}`} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {service.teamName}
                      </div>
                      {service.description && (
                        <div className="text-sm text-gray-500 truncate max-w-xs">
                          {service.description}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {service.position || 'Member'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {service.totalServices}
                    </div>
                    <div className="text-xs text-gray-500">
                      {service.monthsActive} months active
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getFrequencyColor(service.frequencyLevel)}`}>
                      {service.frequencyLevel}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded border ${getStatusColor(service.status)}`}>
                      {service.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div>Joined: {formatDate(service.joinedAt)}</div>
                    <div>Last: {formatDate(service.lastService)}</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ServiceHistory;
